/**
 * Alshallal Pastry Company HR System - Main Layout
 * Combines Sidebar, Topbar, and content area with RTL support
 * Mobile-responsive with off-canvas drawer navigation
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import AIFloatingButton from '@/components/ai/AIFloatingButton';
import ModeChooserDialog from '@/components/auth/ModeChooserDialog';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { pageVariants } from '@/lib/motion';

// Mobile breakpoint - includes iPad portrait (1024px)
// Using 1024 ensures tablet users get mobile-optimized nav
const MOBILE_BREAKPOINT = 1024;

export const MainLayout = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const { isRTL, t } = useLanguage();
  const location = useLocation();
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Auto-close mobile menu when switching to desktop
      if (!mobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Handle ESC key to close mobile menu
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [mobileMenuOpen]);

  // Toggle handlers
  const handleSidebarToggle = useCallback(() => {
    if (isMobile) {
      setMobileMenuOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative h-12 w-12">
            <div className="absolute inset-0 rounded-full border-2 border-brand-orange/20" />
            <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-brand-orange" />
          </div>
          <span className="text-brand-brown font-medium text-sm tracking-wide">
            {t('loading')}
          </span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // The Confidentiality Undertaking comes before everything, including the
  // forced password change: the server 403s every other endpoint until it is
  // accepted, so sending them anywhere else would just show a broken screen.
  if (user?.nda_required) {
    return <Navigate to="/nda" replace />;
  }

  // Redirect to change password if required
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  // "Enter as Employee, or as <your role>?" - after the undertaking and the
  // password change, because those two are conditions of using the system at
  // all. Blocking: the server refuses every route until it is answered, so
  // there is nothing behind this to render.
  if (user?.login_mode_required) {
    return <ModeChooserDialog />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="main-layout">
      {/* Mobile Backdrop - z-45 between content (z-30) and drawer (z-50) */}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-brand-black/60 z-[45] transition-opacity duration-300 backdrop-blur-sm"
          onClick={handleMobileMenuClose}
          aria-hidden="true"
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={handleSidebarToggle}
        isMobile={isMobile}
        mobileOpen={mobileMenuOpen}
        onMobileClose={handleMobileMenuClose}
      />

      {/* Topbar */}
      <Topbar 
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        onMobileMenuToggle={() => setMobileMenuOpen(prev => !prev)}
      />

      {/* Main Content */}
      <main
        className={cn(
          "pt-16 min-h-screen transition-all duration-300",
          // On mobile, content takes full width
          isMobile 
            ? "ml-0 mr-0"
            : sidebarCollapsed 
              ? (isRTL ? "mr-16" : "ml-16")
              : (isRTL ? "mr-64" : "ml-64")
        )}
        data-testid="main-content"
      >
        <div className="p-4 sm:p-6 md:p-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* AI Floating Chat Button - Phase 23 */}
      <AIFloatingButton />
    </div>
  );
};

export default MainLayout;
